import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import "./Header.scss";
import {
  MotifHeader,
  MotifHeaderLogo,
  MotifAvatar,
  MotifVerticalNavigationHeaderButton,
  MotifTooltip,
  MotifDropdownItem,
  MotifDropdown,
  MotifIconButton,
} from "../../assets/lib/motif";
import { HelpIcon } from "../../icons/Help";
import { useDispatch, useSelector } from "react-redux";
import {
  getEngagementById,
  getRegionList,
  setRegion,
} from "../../store/slices/engagementSlice";
import {
  fetchStepperSteps,
} from "../../store/slices/stepperSlice";
import { appName } from "../../util/constants";
import { getPhoto } from "../../store/slices/userSlice";
import { Locker } from "../../icons/locker";
import { routes } from "../../routes/routes.constatns";
import { releaseAlllocks } from "../../store/services/engagement.service";
import { useGetTokenForImage } from "../../hooks/useAppToken";
import { onShowLogOutModal} from "../../store/slices/logoutSlice";
import LogoutModal from "./LogoutModal";

function Avatar({ user, avatar }) {
  const [isDropdownOpen, setDropdown] = useState(false);
  const dispatch = useDispatch();
  const triggerId = "dropdown-trigger-1";

  const handleClickDropdownItem = async () => {
    dispatch(onShowLogOutModal());
  };

  return (
    <MotifDropdown
      ariaLabelledby={triggerId}
      handleOnClose={() => setDropdown(false)}
      onClick={() => setDropdown(!isDropdownOpen)}
      open={isDropdownOpen}
      placement="left"
      trigger={
        <MotifAvatar
          description={user.name}
          size="medium"
          userName={user.name}
          src={avatar}
          interactive={true}
          id={triggerId}
        />
      }
    >
      <MotifDropdownItem
        indexItem={1}
        aria-label="Log out"
        onClick={handleClickDropdownItem}
        type="container"
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
          }}
        >
          <MotifAvatar
            description={user.name}
            size="large"
            userName={user.name}
            src={avatar}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span className="motif-body1-default-bold">{user.name}</span>
            <span className="motif-body1-default-light">{user.username}</span>
          </div>
        </div>
      </MotifDropdownItem>
      <span className="motif-dropdown-horizontal-divider" />
      <MotifDropdownItem
        indexItem={2}
        aria-label="Log out"
        onClick={handleClickDropdownItem}
      >
        Log out
      </MotifDropdownItem>
    </MotifDropdown>
  );
}

function Header({ setSideMenuCollapsed = () => {} }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const state = location.state;
  const popup = location.pathname.includes(routes.decarbProjectsLibrary);
  const user = useSelector((state) => state.user.user);
  const avatar = useSelector((state) => state.user.photo);
  const lockingData = useSelector((state) => state.project?.lockingData);
  const engagement = useSelector((state) => state.engagement?.engagement);
  const regionList = useSelector((state) => state.engagement.regionList);
  const selectedRegion = useSelector(
    (state) => state.engagement.selectedRegion
  );
  const steps = useSelector((state) => state.stepper.steps);
  const [remainingTime, setRemainingTime] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const regionId = searchParams.get("regionId");
  const engagementId = searchParams.get("engagementId");
  const token = useGetTokenForImage();

  async function onLogoClickHandler() {
    await releaseAlllocks({ request: { email: user.username } });
    window.location.href = `${process.env.REACT_APP_URL_REG_ALL}${routes.home}`;
  }

  const userConsent = useSelector(
    (state) => state.engagement?.externalUserFlag
  );

  useEffect(() => {
    if (user && user?.username) {
      if (!avatar && token) dispatch(getPhoto({ token: token }));
    }
  }, [dispatch, user, user?.username, avatar, token]);

  useEffect(() => {
    dispatch(getRegionList());

    if (!steps || steps.length === 0) dispatch(fetchStepperSteps());
  }, [dispatch]);

  let engId = state?.engagementId ?? engagementId;
  if (!engId) {
    engId = engagement?.id;
  }

  useEffect(() => {
    if (engId) {
      dispatch(getEngagementById({ id: engId }));
    }
  }, [engId]);

  useEffect(() => {
    const regId = state?.regionId ?? regionId ?? selectedRegion?.id;

    if (regionList.length && regId) {
      dispatch(
        setRegion({
          regId: parseInt(regId),
        })
      );
    }
  }, [
    regionList.length,
    state?.regionId,
    regionId,
    selectedRegion?.id,
    dispatch,
  ]);

  useEffect(() => {
    if (!lockingData?.data?.lockedTime) {
      setRemainingTime("");
      return;
    }

    const updateRemainingTime = () => {
      const currentTime = new Date(); // User's local time
      const lockedTimeUTC = new Date(lockingData?.data?.lockedTime); // Assuming lockedTime is in UTC
      const lockedTimeLocal = new Date(
        lockedTimeUTC.getTime() - currentTime.getTimezoneOffset() * 60000
      );
      const endTime = new Date(lockedTimeLocal.getTime() + 15 * 60000);

      const timeDiff = endTime - currentTime;
      if (timeDiff >= 0) {
        const minutes = Math.floor(timeDiff / 60000);
        const seconds = Math.floor((timeDiff % 60000) / 1000);
        setRemainingTime(`${minutes}m ${seconds}s`);
      } else {
        setRemainingTime("0m 0s");
      }
    };

    // Immediately update remaining time
    updateRemainingTime();

    // Set interval to update remaining time every second
    const interval = setInterval(updateRemainingTime, 1000);
    return () => clearInterval(interval);
  }, [lockingData]);

  return (
    <>
    <MotifHeader
      className="decarb-header"
      appHeaderName={appName}
      logo={
        <MotifHeaderLogo>
          <a
            aria-label="EY Logo - Home"
            onClick={() => {
              return !popup ? onLogoClickHandler() : null;
            }}
            className="decarb-header-logo"
          >
            <img
              aria-hidden="true"
              alt="LOGO"
              src={require("../../assets/Images/EY_Logo_Beam_RGB_White_Yellow.png")}
            />
          </a>
        </MotifHeaderLogo>
      }
      iconsContainer={
        <div className="decarb-header-right-components">
          <div className="tooling-body">
            {lockingData?.data?.lockStatus === 1 &&
              engagement &&
              lockingData?.data?.lockedBy !== user.username && (
                <MotifTooltip
                  className="tooltip-icon"
                  children={`${lockingData?.data?.lockedBy}\nRemaining time: ${remainingTime}`}
                  placement="auto"
                  hideCloseButton="true"
                  variant="alt"
                  trigger={<Locker />}
                />
              )}
          </div>
          {!userConsent && (
            <MotifIconButton
              onClick={() =>
                window.open(
                  "https://sites.ey.com/sites/DecarbResource/SitePages/Decarb-Resource.aspx",
                  // "mywindow",
                  // "location=0,menubar=0,status=0,scrollbars=0"
                  "_blank"
                )
              }
            >
              <HelpIcon className="help-icon" />
            </MotifIconButton>
          )}
          <Avatar user={user} avatar={avatar} />
        </div>
      }
      leftComponents={
        popup ? (
          <></>
        ) : (
          <div className="decarb-header-left-components">
            <MotifVerticalNavigationHeaderButton
              onClick={setSideMenuCollapsed}
            />
          </div>
        )
      }
    />
    <LogoutModal />
    </>
  );
}

export default Header;
